import React from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';

const notifications = [
  { type: 'call', from: 'Nonprofit Partner', time: '9:42 AM', content: 'Missed Call', icon: '📞' },
  { type: 'slack', from: 'Finance Team', time: '9:38 AM', content: 'Need budget approval for volunteer event', icon: '💬' },
  { type: 'email', from: 'Volunteer Coordinator', time: '9:15 AM', content: 'Re: Re: Re: Event Details', icon: '✉️' },
  { type: 'sms', from: 'Driver', time: '8:57 AM', content: 'Where is the venue again?', icon: '💬' },
  { type: 'calendar', from: 'Team Meeting', time: '8:30 AM', content: 'Volunteer Event Planning - 1hr', icon: '📅' },
];

const automationSteps = [
  { 
    title: 'Event Created', 
    description: 'CORAA creates event in your calendar',
    time: '8:00 AM',
    icon: '📅',
    color: 'bg-blue-500/20',
    border: 'border-blue-500/30'
  },
  { 
    title: 'NPO Notified', 
    description: 'NPOs receive automated invites and check list',
    time: '8:05 AM',
    icon: '📢',
    color: 'bg-purple-500/20',
    border: 'border-purple-500/30'
  },
  { 
    title: 'NPO Approved', 
    description: 'CORAA shortlist and approves NPO',
    time: '8:30 AM',
    icon: '✅',
    color: 'bg-green-500/20',
    border: 'border-green-500/30'
  },
  { 
    title: 'Reminders Sent', 
    description: 'Automated reminders to NPOs',
    time: '9:00 AM',
    icon: '🔔',
    color: 'bg-yellow-500/20',
    border: 'border-yellow-500/30'
  },
  { 
    title: 'Event Started', 
    description: 'CORAA coordinates tracks all external engagements',
    time: '10:00 AM',
    icon: '🚀',
    color: 'bg-green-600/20',
    border: 'border-green-600/30'
  },
];

// Animated counter component
const AnimatedCounter = ({ value, suffix = '', prefix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 2000;
    const stepTime = Math.max(Math.floor(duration / value), 30);
    let current = 0;
    
    const timer = setInterval(() => {
      current += 1;
      setDisplayValue(Math.min(current, value));
      if (current >= value) clearInterval(timer);
    }, stepTime);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return <span>{prefix}{displayValue}{suffix}</span>;
};

const ChaosWithoutCoraa = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAutomation, setShowAutomation] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeTab, setActiveTab] = useState('without');
  const controls = useAnimation();
  const containerRef = useRef(null);
  
  // Metrics data
  const metrics = [
    { value: 87, label: 'Time Saved', suffix: '%' },
    { value: 64, label: 'Reduced Errors', suffix: '%' },
    { value: 12, label: 'Hours/Week', suffix: '+' },
  ];

  // Auto-transition to 'with' panel after 15 seconds
  useEffect(() => {
    const transitionToAutomation = setTimeout(() => {
      setActiveTab('with');
      setShowAutomation(true);
    }, 3000);

    return () => clearTimeout(transitionToAutomation);
  }, []);

  // Auto-advance through automation steps with smooth transitions
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    try {
      timer = setInterval(() => {
        if (activeTab === 'with') {
          setCurrentStep((prev) => (prev + 1) % (automationSteps.length + 1));
        }
      }, 3000);
    } catch (error) {
      console.error('Error in auto-advance timer:', error);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [activeTab]);
  
  // Animate metrics when in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          controls.start('visible');
        }
      },
      { threshold: 0.2 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [controls]);

  // Toggle between chaos and automation views
  useEffect(() => {
    if (activeTab === 'with') {
      if (currentStep >= automationSteps.length) {
        setShowAutomation(false);
        setCurrentStep(0);
      } else if (currentStep > 0) {
        setShowAutomation(true);
      }
    }
  }, [currentStep, activeTab]);
  


  return (
    <section className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden" ref={containerRef}>
      <div className="container mx-auto px-6 relative">
        {/* Floating elements */}
        <motion.div 
          className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full filter blur-3xl -mr-32 -mt-32"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        />
        <motion.div 
          className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl -ml-48 -mb-48"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.08, 0.12, 0.08]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: 2
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Life <span className="text-red-400">Without</span> vs. <span className="text-green-400">With</span> CORAA
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            See the difference CORAA makes in managing your volunteer events
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">

          {/* Content Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Panel - Without CORAA */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{
                opacity: activeTab === 'without' ? 1 : 0.7,
                x: activeTab === 'without' ? 0 : -20,
                scale: activeTab === 'without' ? 1 : 0.98
              }}
              transition={{ duration: 0.5 }}
              className={`bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border ${
                activeTab === 'without' ? 'border-red-500/30' : 'border-gray-700/50'
              }`}
              onMouseEnter={() => activeTab === 'with' && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => setActiveTab('without')}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Without CORAA</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-400">Manual Chaos</span>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {notifications.map((notif, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className={`p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm`}
                  >
                    <div className="flex items-start">
                      <div className="text-xl mr-3">{notif.icon}</div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-semibold text-white">{notif.from}</h4>
                          <span className="text-xs text-gray-400">{notif.time}</span>
                        </div>
                        <p className="text-gray-300 text-sm mt-1">{notif.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div 
                className="mt-8 p-4 bg-red-900/20 border border-red-800/30 rounded-xl"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: notifications.length * 0.1 + 0.5 }}
              >
                <p className="text-red-200 font-medium">
                  This is your life without CORAA. Constant notifications, missed details, and manual coordination.
                
                </p>
              </motion.div>

            </motion.div>
      

            {/* Right Panel - With CORAA */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{
                opacity: activeTab === 'with' ? 1 : 0.7,
                x: activeTab === 'with' ? 0 : 20,
                scale: activeTab === 'with' ? 1 : 0.98
              }}
              transition={{ duration: 0.5 }}
              className={`bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border ${
                activeTab === 'with' ? 'border-green-500/30' : 'border-gray-700/50'
              }`}
              onMouseEnter={() => activeTab === 'without' && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => setActiveTab('with')}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">With CORAA</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-400">Automated Harmony</span>
                </div>
              </div>

              <div className="relative min-h-[400px] mb-8">
                <AnimatePresence mode="wait">
                  {showAutomation ? (
                    <motion.div
                      key="automation"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4"
                    >
                      {automationSteps.map((step, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ 
                            opacity: currentStep >= index ? 1 : 0.5,
                            x: currentStep >= index ? 0 : 20,
                            scale: currentStep === index ? 1.02 : 1
                          }}
                          transition={{ 
                            duration: 0.3,
                            delay: index * 0.1
                          }}
                          className={`p-4 rounded-xl border ${step.border} ${step.color} backdrop-blur-sm`}
                        >
                          <div className="flex items-start">
                            <div className="text-xl mr-3">{step.icon}</div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <h4 className="font-semibold text-white">{step.title}</h4>
                                <span className="text-xs text-gray-400">{step.time}</span>
                              </div>
                              <p className="text-gray-300 text-sm mt-1">{step.description}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="coraa-working"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className="h-full flex flex-col items-center justify-center p-8 text-center"
                    >
                      <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                        <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">CORAA is Working</h3>
                      <p className="text-gray-400 max-w-md">
                        Sit back and relax while CORAA handles all the coordination and communication for your event.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <motion.div 
                className="p-4 bg-green-900/20 border border-green-800/30 rounded-xl mb-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <p className="text-green-200 font-medium">
                  This is your life with CORAA. Automated workflows, clear communication, and peace of mind.
                </p>
              </motion.div>
              
              {/* Metrics Grid */}
              <motion.div 
                className="grid grid-cols-3 gap-4 mb-8"
                initial="hidden"
                animate={controls}
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.2
                    }
                  }
                }}
              >
                {metrics.map((metric, index) => (
                  <motion.div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center border border-white/5"
                    variants={{
                      hidden: { y: 20, opacity: 0 },
                      visible: { 
                        y: 0, 
                        opacity: 1,
                        transition: { duration: 0.5 }
                      }
                    }}
                  >
                    <div className="text-3xl font-bold text-green-400 mb-1">
                      <AnimatedCounter 
                        value={metric.value} 
                        suffix={metric.suffix} 
                      />
                    </div>
                    <div className="text-xs text-gray-400">{metric.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
      {/* Background elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-green-500/5 rounded-full filter blur-3xl"></div>
      </div>
    </section>
  );
};

export default ChaosWithoutCoraa;
